<template>
  <div class="container-fluid">
    <div class="row">
      <FinancesPartnersModal
        v-model="createModal.show"
        :countries="getCountries"
        :isLoading="isCreateModalLoading"
        @hide="hideCreateModal"
        @submit="createFinancePartner"
      />
      <FinancesPartnersModal
        v-model="editModal.show"
        :title="editModal.title"
        :countries="getCountries"
        :data="editModal.data"
        :isLoading="isEditModalLoading"
        @hide="hideEditModal"
        @submit="editFinancePartner"
      />

      <div class="col-12">
        <div class="page-table-header full-height-page-wrapper">
          <PageHeader showBreadcrumbs :page-title="'Partners'">
            <template v-slot:buttons>
              <b-button
                class="button title-container__controls-item"
                :variant="isShowFilter ? 'secondary' : 'info'"
                @click="toggleShowFilter"
              >
                <span class="material-icons icon-image-preview">
                  tune
                </span>
                Filters
              </b-button>
              <b-button
                v-if="$can('create-finances_partners')"
                variant="success"
                class="button"
                @click="showCreateModal"
                :disabled="isDataPageLoading"
              >
                Create
              </b-button>
            </template>
          </PageHeader>

          <Filters
            ref="filtersComponent"
            v-if="$can(permissions.ReadAllFinancesClients)"
            v-show="isShowFilter"
            textButtonSubmit="Filter"
            :data="getTableFilters"
            :isLoading="isDataPageLoading && isInitialDataLoaded"
            @submit="setFilterClients"
          />

          <div v-if="isDataPageLoading && !isInitialDataLoaded" class="content-loading-wrapper">
            <Preloader class="centered-preloader" />
          </div>

          <div v-else class="full-height-content">
            <TableForPage
              v-if="getTableData.length > 0"
              :isDataLoading="isDataPageLoading && isInitialDataLoaded"
              ref="table"
              :tableData="getTableData"
              :fields="getTableFields"
              :perPage="getPartners.per_page"
              :currentPage="getPartners.current_page"
              :totalRows="getPartners.total"
              :isEdit="$can('update_all-finances_partners')"
              :isRemove="$can('update_all-finances_partners')"
              isServerRender
              noLocalSorting
              @remove="removePartner"
              @edit="showEditModal"
              @update="updatePartners"
            />
            <EmptyStateNoResults
              v-else-if="isInitialDataLoaded && !getTableData.length && !getPartners.isAnyData"
              title="No partners yet"
              description="Add partners to manage all clients and business contacts in one place."
              customIcon="partner_exchange"
              customIconClasses="material-symbols-outlined empty-state__icon"
              buttonText="Create Contacts"
              @button-click="showCreateModal"
            />
            <EmptyStateNoResults
              v-else-if="isInitialDataLoaded && !getTableData.length && getPartners.isAnyData"
              @reset-filters="handleResetFilters"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { permissions } from '@mocks/permissions'
import FinanceClientsPartners from '@/mixins/FinanceClientsPartners'
import PageHeader from '@components/PageHeader'
import Filters from '@components/Filter/Filter'
import FinancesPartnersModal from '@components/modals/FinancesPartnersModal'
import TableForPage from '@tables/TableForPage'
import { mapGetters } from 'vuex'
import Preloader from '@components/Preloader'
import EmptyStateNoResults from '@components/EmptyStateNoResults.vue'

export default {
  mixins: [FinanceClientsPartners],
  components: {
    PageHeader,
    FinancesPartnersModal,
    Filters,
    TableForPage,
    Preloader,
    EmptyStateNoResults
  },
  data () {
    return {
      permissions,
      isAllOptionsFilterSet: false,
      isPartnersDataLoaded: false,
      isCreateModalLoading: false,
      isEditModalLoading: false
    }
  },
  computed: {
    isInitialDataLoaded () {
      return this.isAllOptionsFilterSet && this.isPartnersDataLoaded
    },
    ...mapGetters({
      getPartners: 'partners/getPartners'
    }),
    getTableFilters () {
      return this.filters || {}
    },
    getTableFields () {
      const currentFields = this.table.fields.filter(field => field.key !== 'projects')
      return currentFields || []
    },
    getTableData () {
      return this.getPartners.data || []
    },
    getTableRowModal () {
      const currentTableRowModal = this.tableRowModal.fields.filter(field => field.key !== 'project.name')
      return currentTableRowModal || []
    }
  },
  methods: {
    fetchAllData () {
      Promise.all([
        this.fetchPartners(),
        this.fetchCountries()
      ])
        .then(() => {
          this.setAllOptionsFilter()
        })
    },
    setAllOptionsFilter () {
      // Set filter options if needed
      // this.getTableFilters.partners.options = this.getProjects

      this.isAllOptionsFilterSet = true
    },
    fetchPartners () {
      let params = this.table.params.currentPage

      if (this.table.params.sorting) {
        params += this.table.params.sorting
      }
      if (this.table.params.filters) {
        params += this.table.params.filters
      }

      return this._toggleDataPageLoading(() => this.$api.partners.getPartners(params)
      ).then((result) => {
        this.isPartnersDataLoaded = true
        return result
      })
    },
    setFilterClients (data) {
      this.setParamsFilters(data)
      this.setParamsCurrentPage()
      this.fetchPartners()
    },
    setParamsFilters (data) {
      const filters = Object.values(data).reduce(function (state, current) {
        if (current['value']) {
          const value = typeof current['value'] === 'object'
            ? current['value'][current['valueField']] : current['value']
          state += `&filter[${current.name}]=${value}`
        }
        return state
      }, '')

      this.table.params.filters = filters || null
    },
    updatePartners (data) {
      if (!data) return this.fetchPartners()

      const { currentPage, sortDesc, sortBy } = data

      this.setParamsCurrentPage(currentPage)
      this.setParamsSort(sortDesc, sortBy)

      this.fetchPartners()
    },
    async createFinancePartner (fields) {
      const data = this.prepareDataForEditAndCreateRequest(fields)

      this.isCreateModalLoading = true
      try {
        await this.$api.partners.createPartner(data)
        this.$snotify.success(
          'Partner successfully created',
          'Success',
          this.$store.state.snotify
        )
        this.hideCreateModal()
        this.updatePartners()
      } catch (error) {
        this.$snotify.error('Error creating partner', 'Error')
      } finally {
        this.isCreateModalLoading = false
      }
    },
    async editFinancePartner (fields) {
      const data = this.prepareDataForEditAndCreateRequest(fields)

      this.isEditModalLoading = true
      try {
        await this.$api.partners.editPartner(data)
        this.$snotify.success(
          'Partner updated successfully',
          'Success',
          this.$store.state.snotify
        )
        this.hideEditModal()
        this.updatePartners()
      } catch (error) {
        this.$snotify.error('Error updating partner', 'Error')
      } finally {
        this.isEditModalLoading = false
      }
    },
    async removePartner (item) {
      const confirm = await this.$root.$confirm.open(
        'Delete',
        'Are you sure you want to delete the partner?'
      )

      if (!confirm) return

      this._toggleDataPageLoading(async () => {
        const deleteResponse = await this.$api.partners.removePartner(item.id)
        this.$snotify.success(
          deleteResponse.data,
          'Success',
          this.$store.state.snotify
        )
        this.updatePartners()
      })
    },
    handleResetFilters () {
      if (this.$refs.filtersComponent) {
        this.$refs.filtersComponent.resetFilter()
      }
    }
  }
}
</script>
